// ---------------------------------------------------------------------------
// A range slider, for settings with a wide span and no exact right answer.
//
// Three things here are load-bearing rather than decorative.
//
// **It commits on release, not on every pixel.** A drag fires a change event per
// frame, and every settings change broadcasts a full room state to everybody in
// it. So the value the player sees while dragging is local, and `onChange` is
// called once: on pointer up, on key up, or on blur. Nothing else in the app is
// allowed to be this chatty, and neither is this.
//
// **It is a real `<input type="range">`.** Arrow keys, Home, End, Page Up and
// Page Down, the screen reader's slider role, and the platform's own pointer
// handling all come free, and none of them come free from a div with a mousemove
// listener. The native track and thumb are made invisible and the visible ones
// are drawn underneath, which is the only part that is a reimplementation — and
// it exists because the thumb has to sit legibly on the filled half AND the
// empty half, in both themes, which no single pseudo-element fill manages.
//
// **The thumb's target is 44px** even though the thumb is 20px and the track is
// 6px. The native thumb is a transparent 44px square doing nothing but catching
// the finger.
//
// `readOnly` renders the same geometry with no input in it at all, for the
// players who are not the host. Not a disabled slider: a control that moves
// under a finger and then snaps back because the server refused it is worse than
// one that never moved.
//
// **`StopSlider` at the bottom is the same control over a short list of named
// positions** — bot difficulty, which is five words rather than a span of
// numbers. It is a thin wrapper rather than a mode: everything above stays a
// number line, the wrapper is the only thing that knows a value has a name, and
// the one thing it needed from the track is `ticks`.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';

/** Half the thumb, in px. The drawn track is inset by it at both ends so the
 *  knob's centre and the fill's edge agree with where the browser puts the
 *  native thumb. */
const THUMB_INSET = 22;

export interface SliderProps {
  value: number;
  /** Called once per gesture, never per pixel. */
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  label: string;
  /** Draws the label above the track. Off when the surrounding row draws it. */
  showLabel?: boolean;
  /**
   * Renders the readout above the track, e.g. `30s`. It tracks the finger, which
   * is the whole point: the room does not hear the value until the gesture ends,
   * so the person dragging has to be able to see it.
   */
  format?: (value: number) => string;
  /** Spoken value, e.g. `45 seconds`. Without it a reader announces the number. */
  describeValue?: (value: number) => string;
  /** Rendered as static geometry with no control in it. */
  readOnly?: boolean;
  disabled?: boolean;
  /**
   * How many evenly spaced marks to draw under the track, or nothing for a
   * plain one.
   *
   * For a slider whose values are a short list of named positions rather than a
   * span: without them a five-stop control looks like a continuum, and a player
   * has no way to see that there is nowhere between Hard and Expert to land.
   * They are drawn below the track rather than on it, so neither the fill nor
   * the thumb has to be legible against them.
   */
  ticks?: number;
}

export function Slider(props: SliderProps): ReactElement {
  const {
    value,
    onChange,
    min,
    max,
    step = 1,
    label,
    showLabel = false,
    format,
    describeValue,
    readOnly = false,
    disabled = false,
    ticks,
  } = props;

  const [draft, setDraft] = useState(value);
  const [active, setActive] = useState(false);
  const activeRef = useRef(false);

  // A value arriving from the server lands immediately, unless this player is
  // mid-gesture, in which case their own hand wins until they let go.
  useEffect(() => {
    if (activeRef.current) return;
    setDraft(value);
  }, [value]);

  const shown = readOnly ? value : draft;
  const span = max - min;
  const percent = span <= 0 ? 0 : Math.min(100, Math.max(0, ((shown - min) / span) * 100));

  const begin = () => {
    activeRef.current = true;
    setActive(true);
  };

  const commit = () => {
    if (!activeRef.current) return;
    activeRef.current = false;
    setActive(false);
    // Silence is the correct message when nothing moved.
    if (draft !== value) onChange(draft);
  };

  // The pointer is often released outside a 44px-tall control, and a release the
  // input never hears is a value that never reaches the room.
  useEffect(() => {
    if (!active) return;
    window.addEventListener('pointerup', commit);
    window.addEventListener('pointercancel', commit);
    return () => {
      window.removeEventListener('pointerup', commit);
      window.removeEventListener('pointercancel', commit);
    };
  });

  const rail = (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute top-1/2 -translate-y-1/2"
      style={{ left: THUMB_INSET, right: THUMB_INSET }}
    >
      <div className={`h-1.5 rounded-full ${readOnly || disabled ? 'bg-surface-2' : 'bg-raised'}`}>
        <div
          data-slider-fill
          className={`h-full rounded-full ${
            // Blue is what this app paints things you can press. A reading is
            // not one, so it is drawn in the line colour instead.
            readOnly || disabled ? 'bg-line-strong' : 'bg-blue-500'
          } ${
            // No easing under a finger: a fill that lags the thumb reads as the
            // control being slow rather than as the fill being smooth.
            active ? '' : 'transition-[width] duration-150 ease-out'
          }`}
          style={{ width: `${percent}%` }}
        />
      </div>

      {/* Where the stops are. Under the track, in the line colour, so they read
          on the surface rather than having to survive both the fill and the
          empty half of the track. Two marks would be a pointless decoration on
          a control with only its ends, so a `ticks` of under three draws
          nothing. */}
      {ticks === undefined || ticks < 3
        ? null
        : Array.from({ length: ticks }, (_, index) => (
            <span
              key={index}
              data-slider-tick
              className="absolute top-1/2 rounded-full bg-line-strong"
              style={{
                left: `${(index / (ticks - 1)) * 100}%`,
                height: 3,
                width: 3,
                marginLeft: -1.5,
                marginTop: 7,
              }}
            />
          ))}

      {/* No knob at all when there is nothing to grab. A bar is a reading; a bar
          with a knob on it is an invitation, and the answer to that invitation
          would be the server refusing the change.

          The knob is drawn rather than left to `::-webkit-slider-thumb` because
          it straddles the filled half and the empty half and has to read on
          both, in both themes. White disc, blue rim: the rim carries it on the
          light theme's pale track, the disc on the dark theme's. */}
      {readOnly ? null : (
        <span
          data-slider-knob
          className={`absolute top-1/2 rounded-full border-2 border-blue-500 bg-[var(--c-tile-letter)] transition-transform duration-150 ease-out ${
            active ? 'scale-110' : 'scale-100'
          }`}
          style={{
            left: `${percent}%`,
            height: 20,
            width: 20,
            marginLeft: -10,
            marginTop: -10,
          }}
        />
      )}
    </div>
  );

  const header =
    showLabel || format !== undefined ? (
      <div className="flex items-baseline gap-3">
        {showLabel ? <span className="text-[14px] font-medium text-ink">{label}</span> : null}
        {format !== undefined ? (
          // Three digits at 300, one at 5, and the slot is the same either way.
          <span
            data-slider-value
            className={`ml-auto min-w-[5ch] text-right font-mono text-[17px] font-semibold tabular ${
              readOnly || disabled ? 'text-ink' : 'text-blue-200'
            }`}
          >
            {format(shown)}
          </span>
        ) : null}
      </div>
    ) : null;

  if (readOnly) {
    return (
      <div className="flex w-full flex-col gap-1" data-slider-readonly>
        {header}
        <div className="relative h-11 w-full">{rail}</div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-1">
      {header}
      <div className="relative h-11 w-full">
        {rail}
        <input
          type="range"
          aria-label={label}
          aria-valuetext={describeValue?.(shown)}
          min={min}
          max={max}
          step={step}
          value={draft}
          disabled={disabled}
          onPointerDown={begin}
          onKeyDown={begin}
          onChange={(event) => {
            // Local only. The room hears about it when the gesture ends.
            begin();
            setDraft(Number(event.target.value));
          }}
          onKeyUp={commit}
          onPointerUp={commit}
          onBlur={commit}
          className={[
            'absolute inset-0 m-0 h-11 w-full appearance-none bg-transparent p-0',
            disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer',
            // The native track and thumb do nothing but carry the gesture. The
            // thumb keeps its full 44px so a finger has something to catch.
            '[&::-webkit-slider-runnable-track]:h-11 [&::-webkit-slider-runnable-track]:bg-transparent',
            '[&::-webkit-slider-thumb]:h-11 [&::-webkit-slider-thumb]:w-11 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:border-0 [&::-webkit-slider-thumb]:bg-transparent',
            '[&::-moz-range-track]:h-11 [&::-moz-range-track]:bg-transparent',
            '[&::-moz-range-thumb]:h-11 [&::-moz-range-thumb]:w-11 [&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-transparent',
          ].join(' ')}
        />
      </div>
    </div>
  );
}

/**
 * The same slider over a short list of named positions.
 *
 * The value is one of the names; the track underneath is an index into them, so
 * everything the plain slider already does — one commit per gesture, the arrow
 * keys, `readOnly` — is inherited rather than reimplemented, and the stops are
 * the only new idea.
 *
 * **Only the stop under the thumb is named, and the other four are unlabelled
 * marks.** Five words under a 188px track — which is what the host menu's
 * popover has, and the lobby's add panel is not much wider — is either type too
 * small to read or five labels overlapping each other, and the ends are worse
 * still because a label centred on the first stop hangs off the left edge of the
 * card. So the readout above the track carries the name, where the timer sliders
 * already put their value, and the marks below it say how many places there are
 * to stand. The cost is honest: a player cannot see what the far end is called
 * without dragging to it.
 *
 * `labelFor` rather than a list of labels, because the caller already has a
 * label map and passing the two lists separately is a way for them to disagree.
 */
export function StopSlider<T extends string>(props: {
  stops: readonly T[];
  value: T;
  onChange: (value: T) => void;
  labelFor: (value: T) => string;
  label: string;
  showLabel?: boolean;
  readOnly?: boolean;
  disabled?: boolean;
}): ReactElement {
  const { stops, value, onChange, labelFor, label, showLabel, readOnly, disabled } = props;

  // A value that is not one of the stops sits at the first one rather than off
  // the end of the track: the caller decides what an absent value means, and
  // `indexOf` returning -1 must not draw a thumb outside the rail.
  const index = Math.max(0, stops.indexOf(value));
  const nameAt = (position: number): string => {
    const stop = stops[Math.min(stops.length - 1, Math.max(0, Math.round(position)))];
    return stop === undefined ? '' : labelFor(stop);
  };

  return (
    <Slider
      label={label}
      showLabel={showLabel}
      value={index}
      onChange={(next) => {
        const stop = stops[next];
        if (stop !== undefined) onChange(stop);
      }}
      min={0}
      max={Math.max(0, stops.length - 1)}
      step={1}
      ticks={stops.length}
      format={nameAt}
      describeValue={nameAt}
      readOnly={readOnly}
      disabled={disabled}
    />
  );
}
